import { useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * MultiSelectFilter — pill-style multi-select dropdown used across all
 * CRM list pages. Renders active selections as chips inside the trigger
 * button; opens a searchable popover of options with a Clear action.
 *
 * @param label         Trigger label shown when no selection is active.
 * @param options       Array of `{ value, label }` (or strings — coerced).
 * @param value         Array of selected values.
 * @param onChange(v[]) Called with the new array.
 * @param testId        `data-testid` root for the trigger + items.
 * @param className     Extra classes on the trigger.
 */
export function MultiSelectFilter({
  label,
  options,
  value = [],
  onChange,
  testId,
  className,
  emptyText = "No matches.",
  width = "min-w-[220px]",
}) {
  const [open, setOpen] = useState(false);
  const opts = (options || []).map((o) =>
    typeof o === "string" ? { value: o, label: o } : o,
  );
  const selectedSet = new Set(value);

  const toggle = (v) => {
    if (selectedSet.has(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const selectedLabels = opts
    .filter((o) => selectedSet.has(o.value))
    .map((o) => o.label);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "h-9 justify-between font-normal",
            width,
            className,
          )}
          data-testid={testId ? `${testId}-trigger` : undefined}
        >
          <span className="flex items-center gap-1 truncate">
            {selectedLabels.length === 0 ? (
              <span className="text-slate-500">{label}</span>
            ) : selectedLabels.length <= 2 ? (
              selectedLabels.map((l) => (
                <Badge
                  key={l}
                  variant="secondary"
                  className="max-w-[110px] truncate px-1.5 py-0 text-[11px]"
                >
                  {l}
                </Badge>
              ))
            ) : (
              <Badge variant="secondary" className="px-1.5 py-0 text-[11px]">
                {selectedLabels.length} selected
              </Badge>
            )}
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {selectedLabels.length > 0 && (
              <span
                onClick={clear}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") clear(e); }}
                className="rounded-sm hover:bg-slate-200 p-0.5 cursor-pointer"
                data-testid={testId ? `${testId}-clear` : undefined}
                role="button"
                tabIndex={0}
                aria-label="Clear filter"
              >
                <X className="w-3.5 h-3.5" />
              </span>
            )}
            <ChevronDown className="w-4 h-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}…`} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {opts.map((o) => {
                const isSel = selectedSet.has(o.value);
                return (
                  <CommandItem
                    key={o.value}
                    value={o.label}
                    onSelect={() => toggle(o.value)}
                    data-testid={testId ? `${testId}-item-${o.value}` : undefined}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSel ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {o.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default MultiSelectFilter;
